import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { format, differenceInCalendarDays, addDays, parseISO } from "date-fns";
import {
  MapPin, Share2, Heart, Camera, Star, ShieldCheck, Award, Users, BedDouble, DoorOpen,
  ChefHat, ChevronDown, ChevronLeft, ChevronRight, X, MessageCircle, Navigation, Check,
  Wifi, Car, UtensilsCrossed, Wind, Tv, Coffee, Waves, Trees, PawPrint, Bath, BatteryCharging,
  Zap, Lock, ShieldQuestion, CalendarClock, Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StayHeader } from "@/components/stays/StayHeader";
import { PropertyMap } from "@/components/stays/PropertyMap";
import { BookingFields } from "@/components/stays/booking/BookingFields";
import { PriceBreakdown } from "@/components/stays/booking/PriceBreakdown";
import { TrustBadges } from "@/components/stays/booking/TrustBadges";
import Footer from "@/components/Footer";
import { getAvailability, getFeeQuote, bookingError, fmtAmount, istToday } from "@/lib/stayflowApi";
import { guestLimits } from "@/lib/propertyRules";
import { isWishlisted, toggleWishlist } from "@/lib/wishlist";

const iso = (d) => format(d, "yyyy-MM-dd");

/* ---------------- shared bits ---------------- */
const GoogleG = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
  </svg>
);

const Stars = ({ value = 0 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(value) ? "fill-[#F59E0B] text-[#F59E0B]" : "fill-black/10 text-black/10"}`} aria-hidden="true" />
    ))}
  </span>
);

// Match a keyword at a word boundary (start of a word) to avoid false hits
// like "ac" inside "Attached Bathroom" or "bed" inside a longer unrelated word.
const kwMatch = (text, kw) => {
  const t = (text || "").toLowerCase();
  const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${esc}`, "i").test(t);
};

const AM_ICONS = [
  { kw: ["wifi", "wi-fi", "internet"], icon: Wifi },
  { kw: ["parking", "car"], icon: Car },
  { kw: ["kitchen"], icon: UtensilsCrossed },
  { kw: ["breakfast", "meal", "food"], icon: Coffee },
  { kw: ["ac", "air condition"], icon: Wind },
  { kw: ["tv", "television"], icon: Tv },
  { kw: ["pool", "swim"], icon: Waves },
  { kw: ["bath", "hot water", "geyser", "attached"], icon: Bath },
  { kw: ["bed", "king", "queen"], icon: BedDouble },
  { kw: ["power", "backup", "inverter", "generator"], icon: BatteryCharging },
  { kw: ["garden", "lawn", "nature", "sitting", "space"], icon: Trees },
  { kw: ["pet"], icon: PawPrint },
];
const amIcon = (a) => (AM_ICONS.find((x) => x.kw.some((k) => kwMatch(a, k))) || { icon: Check }).icon;

const HIGHLIGHTS = [
  { kw: ["wifi", "wi-fi", "internet"], icon: Wifi, title: "High-speed WiFi", blurb: "Stream and work with a stable connection." },
  { kw: ["breakfast", "meal", "food"], icon: ChefHat, title: "Home-cooked meals", blurb: "Authentic local flavours on request." },
  { kw: ["kitchen"], icon: UtensilsCrossed, title: "Kitchen access", blurb: "Cook your own meals whenever you like." },
  { kw: ["parking", "car"], icon: Car, title: "Free parking", blurb: "Hassle-free parking right at the stay." },
  { kw: ["ac", "air condition"], icon: Wind, title: "Air conditioning", blurb: "Cool comfort through warm afternoons." },
  { kw: ["power", "backup", "inverter", "generator"], icon: BatteryCharging, title: "24/7 power backup", blurb: "Uninterrupted power, day and night." },
  { kw: ["garden", "lawn", "nature"], icon: Trees, title: "Green surroundings", blurb: "Open space to relax and unwind." },
  { kw: ["hot water", "geyser", "bath"], icon: Bath, title: "Hot water", blurb: "Warm showers whenever you need one." },
  { kw: ["tv", "television"], icon: Tv, title: "Entertainment", blurb: "Kick back with your favourite shows." },
  { kw: ["pool", "swim"], icon: Waves, title: "Swimming pool", blurb: "Take a refreshing dip on-site." },
];
const pickHighlights = (amenities = []) => {
  const out = [];
  for (const a of amenities) {
    const meta = HIGHLIGHTS.find((h) => h.kw.some((k) => kwMatch(a, k)));
    if (meta && !out.some((o) => o.meta === meta)) out.push({ title: a, meta });
    if (out.length === 4) break;
  }
  return out;
};

/* ---------------- full-screen lightbox ---------------- */
const Lightbox = ({ images, open, index, onClose, onIndex }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % images.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, images.length, onClose, onIndex]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-full w-screen h-[100dvh] p-0 gap-0 rounded-none bg-ink border-none overflow-hidden" data-testid="ddp-lightbox">
        <DialogTitle className="sr-only">All photos</DialogTitle>
        <div className="relative h-full flex items-center justify-center">
          <img src={images[index]} alt={`Photo ${index + 1}`} className="max-h-[86vh] max-w-[92vw] object-contain" />
          <div className="absolute top-4 left-6 text-white/80 text-sm font-medium tabular-nums">{index + 1} / {images.length}</div>
          <button onClick={onClose} data-testid="ddp-lightbox-close" className="absolute top-4 right-6 rounded-full bg-white/15 hover:bg-white/25 text-white p-2.5 transition-colors"><X className="w-5 h-5" /></button>
          {images.length > 1 && (
            <>
              <button onClick={() => onIndex((index - 1 + images.length) % images.length)} data-testid="ddp-lightbox-prev" className="absolute left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/25 text-white p-3 transition-colors"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={() => onIndex((index + 1) % images.length)} data-testid="ddp-lightbox-next" className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/25 text-white p-3 transition-colors"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
        </div>
        <div className="shrink-0 bg-black/40 border-t border-white/10 px-4 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-lenis-prevent>
          {images.map((src, i) => (
            <button key={src} onClick={() => onIndex(i)} className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={src} alt="" className="w-20 h-14 object-cover" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- amenities modal ---------------- */
const AmenitiesModal = ({ open, onOpenChange, amenities }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg rounded-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="ddp-amenities-modal">
      <DialogTitle className="font-heading font-extrabold text-2xl text-ink">What this place offers</DialogTitle>
      <div className="overflow-y-auto pr-1 -mr-1 mt-2 divide-y divide-black/5" data-lenis-prevent>
        {amenities.map((a) => {
          const Icon = amIcon(a);
          return (
            <div key={a} className="flex items-center gap-3.5 py-3.5">
              <Icon className="w-5 h-5 text-ink shrink-0" />
              <span className="text-sm text-ink capitalize">{a}</span>
            </div>
          );
        })}
      </div>
    </DialogContent>
  </Dialog>
);

/* ---------------- reservation sidebar ---------------- */
const ReservationSidebar = ({ rooms, propertyId, rev, sp }) => {
  const navigate = useNavigate();
  const today = useMemo(() => istToday(), []);
  const windowEnd = useMemo(() => addDays(today, 180), [today]);

  const sortedRooms = useMemo(() => [...rooms].sort((a, b) => (a.price_per_night || 0) - (b.price_per_night || 0) || String(a.name).localeCompare(String(b.name))), [rooms]);  const [roomId, setRoomId] = useState(() => sortedRooms[0]?.id);
  useEffect(() => { if (!roomId && sortedRooms[0]) setRoomId(sortedRooms[0].id); }, [sortedRooms, roomId]);
  const room = sortedRooms.find((r) => r.id === roomId) || sortedRooms[0];

  const [range, setRange] = useState(() => {
    const ci = sp.get("checkIn"), co = sp.get("checkOut");
    try {
      if (ci && co) {
        const from = parseISO(ci), to = parseISO(co);
        if (from >= today && to > from) return { from, to };
      }
    } catch { /* ignore */ }
    return undefined;
  });
  const [adults, setAdults] = useState(() => Math.max(1, Number(sp.get("adults")) || Number(sp.get("guests")) || 2));
  const [children, setChildren] = useState(() => Math.max(0, Number(sp.get("children")) || 0));
  const { maxAdults, maxChildren, childRuleText } = guestLimits(room);
  useEffect(() => {
    if (!room) return; // limits are unknown until the room loads — don't clamp yet
    if (adults > maxAdults) setAdults(maxAdults);
    if (children > maxChildren) setChildren(maxChildren);
  }, [room, maxAdults, maxChildren, adults, children]);

  const availQ = useQuery({
    queryKey: ["availability", roomId],
    queryFn: () => getAvailability(roomId, iso(today), iso(windowEnd)),
    enabled: !!roomId,
    staleTime: 60 * 1000,
    retry: 1,
  });
  const busyDates = useMemo(
    () => (availQ.data?.busy_days || []).map((s) => { const d = parseISO(s); d.setHours(0, 0, 0, 0); return d; }),
    [availQ.data]
  );

  const price = room?.price_per_night_incl_gst;
  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;

  // Prices, GST and the payable total always come from the server's quote.
  const quoteQ = useQuery({
    queryKey: ["feeQuote", roomId, range?.from ? iso(range.from) : null, range?.to ? iso(range.to) : null, adults, children],
    queryFn: () => getFeeQuote({ room_id: roomId, check_in: iso(range.from), check_out: iso(range.to), adults, children }),
    enabled: !!roomId && nights > 0,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const goReserve = () => {
    const params = new URLSearchParams();
    params.set("propertyId", propertyId);
    params.set("roomId", roomId);
    if (range?.from) params.set("checkIn", iso(range.from));
    if (range?.to) params.set("checkOut", iso(range.to));
    params.set("adults", String(adults));
    params.set("children", String(children));
    navigate(`/book/confirm?${params.toString()}`);
  };

  return (
    <div id="reserve-card" className="rounded-3xl border border-black/10 bg-white shadow-[0_18px_60px_rgba(20,20,20,0.12)] p-6 sticky top-24 z-20" data-testid="ddp-reserve-card">
      {/* room selector */}
      {sortedRooms.length > 1 && (
        <div className="relative mb-4">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-faint mb-1.5">Room type</label>
          <div className="relative">
            <select
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              data-testid="ddp-room-select"
              className="w-full appearance-none rounded-2xl border border-black/20 bg-white pl-4 pr-10 py-3 text-sm font-semibold text-ink cursor-pointer hover:border-ink transition-colors focus:outline-none focus:ring-2 focus:ring-brand/30"
            >
              {sortedRooms.map((r) => (
                <option key={r.id} value={r.id}>{`${r.name} — ${fmtAmount(r.price_per_night_incl_gst)}/night incl. GST`}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-ink absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      )}

      {/* price header */}
      <div className="flex items-center justify-between gap-3">
        <p><span className="font-heading font-extrabold text-[26px] text-ink" data-testid="ddp-price">{fmtAmount(price)}</span><span className="text-ink-soft text-sm"> / night incl. GST</span></p>
        {rev?.rating > 0 && (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-ink">
            <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /> {Number(rev.rating).toFixed(2)}
            <span className="font-normal text-ink-faint">· {rev.total} review{rev.total !== 1 ? "s" : ""}</span>
          </span>
        )}
      </div>

      <div className="mt-4">
        <BookingFields
          range={range} setRange={setRange} today={today} windowEnd={windowEnd}
          busyDates={busyDates} availLoading={availQ.isLoading} availError={availQ.isError}
          adults={adults} setAdults={setAdults} childrenCount={children} setChildrenCount={setChildren} maxAdults={maxAdults} maxChildren={maxChildren} childRuleText={childRuleText}
        />
      </div>

      <button onClick={goReserve} data-testid="ddp-reserve-btn" className="mt-5 w-full rounded-xl bg-brand hover:bg-brand-dark text-white font-heading font-extrabold py-3.5 text-base shadow-lg shadow-brand/25 active:scale-[0.98] transition">
        Reserve Stay
      </button>
      <p className="text-center text-xs text-ink-faint mt-2">You won't be charged yet</p>

      {nights > 0 && (
        <div className="mt-5 border-t border-black/10 pt-4" data-testid="ddp-bill">
          <PriceBreakdown quote={quoteQ.data || null} nights={nights} loading={quoteQ.isFetching} error={quoteQ.isError ? bookingError(quoteQ.error) : ""} idPrefix="ddp-" />
        </div>
      )}

      {/* transparent payment split */}
      <div className="mt-4 rounded-xl bg-paper-muted/70 border border-black/5 p-3.5 space-y-2" data-testid="ddp-payment-split">
        <div className="flex items-start gap-2.5">
          <span className="w-7 h-7 shrink-0 rounded-lg bg-brand-soft text-brand flex items-center justify-center"><Zap className="w-4 h-4" /></span>
          <p className="text-xs text-ink-soft leading-relaxed"><span className="font-semibold text-ink">Pay a small advance now</span> to instantly confirm your dates via secure UPI or card.</p>
        </div>
        <div className="flex items-start gap-2.5">
          <span className="w-7 h-7 shrink-0 rounded-lg bg-brand-soft text-brand flex items-center justify-center"><CalendarClock className="w-4 h-4" /></span>
          <p className="text-xs text-ink-soft leading-relaxed"><span className="font-semibold text-ink">Balance at check-in</span>, paid directly to your host on arrival.</p>
        </div>
      </div>

      <TrustBadges className="mt-4 pt-4 border-t border-black/5" />
    </div>
  );
};

/* ---------------- main desktop page ---------------- */
const PropertyDetailDesktop = ({ p, rooms = [], geo, rev, reviewsLoading, onShare, propertyId, sp }) => {
  const images = useMemo(() => {
    const roomPics = rooms.flatMap((r) => [r.cover_image, ...((r.photos || []).map((ph) => ph.url))]);
    return [...new Set([p.cover_image, ...roomPics].filter(Boolean))];
  }, [p.cover_image, rooms]);

  const [lightOpen, setLightOpen] = useState(false);
  const [lightIdx, setLightIdx] = useState(0);
  const openLight = (i) => { setLightIdx(i); setLightOpen(true); };

  const [saved, setSaved] = useState(() => isWishlisted(propertyId));
  useEffect(() => {
    const on = () => setSaved(isWishlisted(propertyId));
    window.addEventListener("sf-wishlist", on);
    return () => window.removeEventListener("sf-wishlist", on);
  }, [propertyId]);

  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);

  // aggregate real amenities from property + rooms
  const allAmenities = useMemo(() => {
    const seen = new Set(); const out = [];
    [...(p.amenities || []), ...rooms.flatMap((r) => r.amenities || [])].forEach((a) => {
      const k = (a || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (a && k && !seen.has(k)) { seen.add(k); out.push(a); }
    });
    return out;
  }, [p.amenities, rooms]);

  const highlights = useMemo(() => pickHighlights(allAmenities), [allAmenities]);
  const capacity = rooms.reduce((s, r) => s + (Number(r.capacity) || 0), 0);
  const beds = rooms.reduce((s, r) => s + (Number(r.beds) || 0), 0);
  const loc = [geo?.city || p.city, geo?.state || p.state, p.country].filter(Boolean).join(", ");
  const minPrice = rooms.length ? Math.min(...rooms.map((r) => r.price_per_night_incl_gst || Infinity)) : p.min_price_incl_gst;

  const phoneDigits = (p.phone || "").replace(/\D/g, "");
  const waNumber = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi! I have a question about ${p.name} on StayFlow.`)}` : null;

  // ---- sub-nav ----
  const sections = useMemo(() => [
    { id: "overview", label: "Overview" },
    rooms.length > 0 && { id: "rooms", label: "Rooms" },
    allAmenities.length > 0 && { id: "amenities", label: "Amenities" },
    { id: "reviews", label: "Reviews" },
    geo?.lat != null && { id: "location", label: "Location" },
    { id: "host", label: "Host & Rules" },
  ].filter(Boolean), [rooms.length, allAmenities.length, geo]);

  const [activeSec, setActiveSec] = useState("overview");
  const [showTicker, setShowTicker] = useState(false);
  const secRefs = useRef({});
  const lockRef = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      setShowTicker(window.scrollY > 520);
      if (Date.now() < lockRef.current) return;
      let cur = sections[0].id;
      for (const s of sections) {
        const el = secRefs.current[s.id];
        if (el && el.getBoundingClientRect().top <= 160) cur = s.id;
      }
      setActiveSec(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const jump = useCallback((id) => {
    const el = secRefs.current[id];
    if (!el) return;
    lockRef.current = Date.now() + 900;
    setActiveSec(id);
    const top = el.getBoundingClientRect().top + window.scrollY - 120;
    if (window.__lenis) window.__lenis.scrollTo(top);
    else window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const scrollToReserve = () => {
    const el = document.getElementById("reserve-card");
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 100;
    if (window.__lenis) window.__lenis.scrollTo(top); else window.scrollTo({ top, behavior: "smooth" });
  };

  const secProps = (id) => ({ ref: (el) => { secRefs.current[id] = el; }, "data-sec": id, className: "scroll-mt-32" });

  return (
    <div data-testid="ddp-page">
      <StayHeader />

      {/* ---- Title & social actions ---- */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-end justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-2xl lg:text-3xl font-extrabold text-ink tracking-tight font-heading">{p.name}</h1>
            {loc && (
              <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-ink-soft">
                <MapPin className="w-4 h-4 text-brand shrink-0" /> {loc}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onShare} data-testid="ddp-share-btn"
              className="px-3.5 py-2 rounded-xl border border-black/15 text-ink text-xs font-semibold hover:bg-paper flex items-center gap-1.5 transition-colors">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={() => setSaved(toggleWishlist(propertyId))} data-testid="ddp-wishlist-btn"
              className="px-3.5 py-2 rounded-xl border border-black/15 text-ink text-xs font-semibold hover:bg-paper flex items-center gap-1.5 transition-colors">
              <motion.span key={String(saved)} initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 15 }}>
                <Heart className={`w-4 h-4 transition-colors ${saved ? "fill-brand text-brand" : ""}`} />
              </motion.span>
              {saved ? "Saved" : "Save to Wishlist"}
            </button>
          </div>
        </div>
      </div>

      {/* ---- Bento gallery ---- */}
      <div className="max-w-7xl mx-auto px-6">
        {images.length > 0 && (
          <div className="h-[460px] w-full rounded-3xl overflow-hidden grid grid-cols-4 grid-rows-2 gap-2.5 relative" data-testid="ddp-bento">
            <button onClick={() => openLight(0)} className={`relative overflow-hidden group cursor-pointer ${images.length === 1 ? "col-span-4 row-span-2" : "col-span-2 row-span-2"}`}>
              <img src={images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500 ease-out" />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
            </button>
            {images.slice(1, 5).map((src, i) => (
              <button key={src} onClick={() => openLight(i + 1)} className="relative overflow-hidden group cursor-pointer col-span-1 row-span-1">
                <img src={src} alt={`${p.name} ${i + 2}`} className="w-full h-full object-cover group-hover:scale-105 transition duration-500 ease-out" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
              </button>
            ))}
            <button onClick={() => openLight(0)} data-testid="ddp-show-all-photos"
              className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-md hover:bg-white text-ink font-semibold px-4 py-2.5 rounded-xl shadow-lg border border-black/10 text-xs flex items-center gap-2 transition">
              <Camera className="w-4 h-4" /> Show all {images.length} photos
            </button>
          </div>
        )}
      </div>

      {/* ---- Sticky sub-nav ---- */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-black/10 mt-6" data-testid="ddp-subnav">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-1">
            {sections.map((s) => (
              <button key={s.id} onClick={() => jump(s.id)} data-testid={`ddp-nav-${s.id}`}
                className={`relative px-3.5 py-1.5 text-sm font-semibold transition-colors ${activeSec === s.id ? "text-ink" : "text-ink-faint hover:text-ink-soft"}`}>
                {s.label}
                {activeSec === s.id && <motion.span layoutId="ddp-underline" className="absolute left-3.5 right-3.5 -bottom-[13px] h-0.5 bg-brand rounded-full" />}
              </button>
            ))}
          </div>
          <AnimatePresence>
            {showTicker && (
              <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                className="flex items-center gap-4" data-testid="ddp-ticker">
                <div className="text-right leading-tight">
                  <p className="font-heading font-extrabold text-ink">{fmtAmount(minPrice)}<span className="text-xs font-sans font-normal text-ink-soft"> / night incl. GST</span></p>
                  {rev?.rating > 0 && <p className="text-xs text-ink-soft inline-flex items-center gap-1 justify-end"><Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" /> {Number(rev.rating).toFixed(2)} ({rev.total})</p>}
                </div>
                <button onClick={scrollToReserve} data-testid="ddp-ticker-reserve"
                  className="bg-brand hover:bg-brand-dark text-white font-bold px-6 py-2 rounded-xl text-xs active:scale-95 transition">
                  Reserve Now
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ---- 2-column layout ---- */}
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-12 gap-12 items-start">
        {/* LEFT */}
        <div className="col-span-7">
          {/* Overview */}
          <section {...secProps("overview")} data-testid="ddp-overview">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1.5 text-xs font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> StayFlow Verified Homestay</span>
              {rev?.rating >= 4.8 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted text-ink px-3 py-1.5 text-xs font-semibold"><Award className="w-3.5 h-3.5 text-brand" /> Top-Rated Host</span>}
            </div>

            {(capacity > 0 || rooms.length > 0 || beds > 0) && (
              <div className="mt-4 flex flex-wrap gap-2.5">
                {capacity > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3.5 py-1.5 text-sm font-medium text-ink"><Users className="w-4 h-4 text-brand" /> Up to {capacity} guests</span>}
                {rooms.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3.5 py-1.5 text-sm font-medium text-ink"><DoorOpen className="w-4 h-4 text-brand" /> {rooms.length} bedroom{rooms.length !== 1 ? "s" : ""}</span>}
                {beds > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3.5 py-1.5 text-sm font-medium text-ink"><BedDouble className="w-4 h-4 text-brand" /> {beds} bed{beds !== 1 ? "s" : ""}</span>}
              </div>
            )}

            {highlights.length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-3" data-testid="ddp-highlights">
                {highlights.map(({ title, meta }) => {
                  const Icon = meta.icon;
                  return (
                    <div key={title} className="flex items-start gap-3.5 rounded-2xl border border-black/5 bg-white p-4 shadow-sm">
                      <span className="w-11 h-11 shrink-0 rounded-xl bg-brand-soft text-brand flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                      <span className="min-w-0">
                        <span className="block font-heading font-bold text-sm text-ink">{meta.title}</span>
                        <span className="block text-xs text-ink-soft mt-0.5">{meta.blurb}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Description */}
          {p.description && (
            <section className="mt-10 border-t border-black/5 pt-8">
              <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">About this place</h2>
              <p className={`mt-4 text-ink-soft leading-relaxed whitespace-pre-line ${showFullDesc ? "" : "line-clamp-[5]"}`}>{p.description}</p>
              {p.description.length > 260 && (
                <button onClick={() => setShowFullDesc((v) => !v)} data-testid="ddp-desc-toggle" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-ink underline underline-offset-4">
                  {showFullDesc ? "Show less" : "Show more"} <ChevronDown className={`w-4 h-4 transition-transform ${showFullDesc ? "rotate-180" : ""}`} />
                </button>
              )}
            </section>
          )}

          {/* Rooms */}
          {rooms.length > 0 && (
            <section {...secProps("rooms")} className="mt-10 border-t border-black/5 pt-8 scroll-mt-32" data-testid="ddp-rooms">
              <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">Where you'll sleep</h2>
              <div className="mt-5 grid grid-cols-3 gap-4">
                {rooms.map((r) => {
                  const cover = r.cover_image || (r.photos || [])[0]?.url;
                  const ensuite = (r.amenities || []).some((a) => /attached|ensuite|private bath/i.test(a));
                  return (
                    <Link key={r.id} to={`/stays/${propertyId}/${r.id}`} data-testid={`ddp-room-${r.id}`}
                      className="bg-paper border border-black/5 rounded-2xl p-4 space-y-2 hover:shadow-md hover:-translate-y-0.5 transition group">
                      <div className="aspect-video rounded-xl overflow-hidden bg-paper-muted">
                        {cover && <img src={cover} alt={r.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                      </div>
                      <p className="font-heading font-bold text-sm text-ink">{r.name}</p>
                      <p className="text-xs text-ink-soft">{[r.type, r.beds ? `${r.beds} bed${r.beds > 1 ? "s" : ""}` : null, r.capacity ? `sleeps ${r.capacity}` : null].filter(Boolean).join(" · ") || "Cozy private room"}</p>
                      <div className="flex items-center justify-between pt-1">
                        {ensuite && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold"><Bath className="w-3 h-3" /> Ensuite</span>}
                        <span className="ml-auto text-ink"><span className="font-heading font-extrabold text-sm">{fmtAmount(r.price_per_night_incl_gst)}</span><span className="text-[11px] text-ink-soft"> /night incl. GST</span></span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Amenities */}
          {allAmenities.length > 0 && (
            <section {...secProps("amenities")} className="mt-10 border-t border-black/5 pt-8 scroll-mt-32" data-testid="ddp-amenities">
              <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">What this place offers</h2>
              <div className="mt-5 grid grid-cols-2 gap-y-3 gap-x-6">
                {allAmenities.slice(0, 8).map((a) => {
                  const Icon = amIcon(a);
                  return (
                    <div key={a} className="flex items-center gap-3 py-1 text-sm text-ink capitalize">
                      <Icon className="w-5 h-5 text-ink shrink-0" /> {a}
                    </div>
                  );
                })}
              </div>
              {allAmenities.length > 8 && (
                <button onClick={() => setAmenitiesOpen(true)} data-testid="ddp-amenities-all"
                  className="mt-5 border border-ink text-ink font-semibold px-5 py-2.5 rounded-xl hover:bg-paper text-sm transition-colors">
                  Show all {allAmenities.length} amenities
                </button>
              )}
            </section>
          )}

          {/* Reviews */}
          <section {...secProps("reviews")} className="mt-10 border-t border-black/5 pt-8 scroll-mt-32" data-testid="ddp-reviews">
            <div className="flex items-center gap-3">
              {rev?.rating > 0 ? (
                <>
                  <span className="inline-flex items-baseline gap-1.5 text-4xl font-extrabold text-ink font-heading">
                    <Star className="w-7 h-7 fill-[#F59E0B] text-[#F59E0B] self-center" /> {Number(rev.rating).toFixed(2)}
                  </span>
                  <div className="leading-tight">
                    <p className="font-semibold text-ink">Guest favourite</p>
                    <p className="text-sm text-ink-soft">Based on {rev.total} verified stay{rev.total !== 1 ? "s" : ""}</p>
                  </div>
                  {rev.place_url && (
                    <a href={rev.place_url} target="_blank" rel="noreferrer" data-testid="ddp-google-attr"
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-paper transition-colors">
                      <GoogleG className="w-3.5 h-3.5" /> Verified via Google
                    </a>
                  )}
                </>
              ) : (
                <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">Guest reviews</h2>
              )}
            </div>

            {reviewsLoading && <div className="mt-5 grid grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="h-32 rounded-2xl bg-paper-muted animate-pulse" />)}</div>}

            {!reviewsLoading && (rev?.reviews || []).length === 0 && (
              <div className="mt-5 rounded-2xl border border-black/5 bg-white p-8 text-center shadow-sm" data-testid="ddp-reviews-empty">
                <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-brand-dark px-3.5 py-1.5 text-sm font-semibold"><Sparkles className="w-4 h-4" /> New listing</span>
                <p className="mt-3 text-sm text-ink-soft">This homestay just joined StayFlow. Be one of the first guests to review it.</p>
              </div>
            )}

            {!reviewsLoading && (rev?.reviews || []).length > 0 && (
              <div className="mt-5 grid grid-cols-2 gap-4">
                {rev.reviews.slice(0, 6).map((r, i) => (
                  <ReviewCard key={`${r.author_name}-${i}`} r={r} />
                ))}
              </div>
            )}
          </section>

          {/* Location */}
          {geo?.lat != null && (
            <section {...secProps("location")} className="mt-10 border-t border-black/5 pt-8 scroll-mt-32" data-testid="ddp-location">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">Where you'll stay</h2>
                {loc && <p className="text-sm text-ink-soft">{loc}</p>}
              </div>
              {geo.address && <p className="text-sm text-ink-soft mb-3 flex items-start gap-1.5"><MapPin className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {geo.address}</p>}
              <div className="h-80 rounded-2xl border border-black/10 overflow-hidden relative isolate shadow-sm">
                <PropertyMap lat={geo.lat} lng={geo.lng} name={p.name} height={320} />
                {geo.location_url && (
                  <a href={geo.location_url} target="_blank" rel="noreferrer" data-testid="ddp-open-gmaps"
                    className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md hover:bg-white text-ink font-semibold px-4 py-2.5 rounded-xl shadow-lg border border-black/10 text-xs flex items-center gap-2 transition z-20">
                    <Navigation className="w-4 h-4 text-brand" /> Open in Google Maps
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Host & Rules */}
          <section {...secProps("host")} className="mt-10 border-t border-black/5 pt-8 scroll-mt-32" data-testid="ddp-host">
            <h2 className="font-heading font-extrabold text-2xl text-ink tracking-tight">Meet your host</h2>
            <div className="mt-5 bg-paper border border-black/5 rounded-3xl p-6 flex items-center justify-between shadow-sm gap-6">
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative shrink-0">
                  <div className="w-16 h-16 rounded-full bg-ink text-white flex items-center justify-center font-heading font-extrabold text-2xl">
                    {(p.name || "S").slice(0, 1).toUpperCase()}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center"><ShieldCheck className="w-3.5 h-3.5 text-white" /></span>
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-bold text-lg text-ink truncate">{p.name}</p>
                  <p className="text-sm text-ink-soft">Verified StayFlow partner · Local host</p>
                </div>
              </div>
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer" data-testid="ddp-whatsapp-host"
                  className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl flex items-center gap-2 text-sm active:scale-95 transition">
                  <MessageCircle className="w-4 h-4" /> Message Host on WhatsApp
                </a>
              ) : p.email ? (
                <a href={`mailto:${p.email}?subject=${encodeURIComponent(`Question about ${p.name} on StayFlow`)}`} data-testid="ddp-email-host"
                  className="shrink-0 bg-ink hover:bg-black text-white font-semibold px-5 py-3 rounded-xl flex items-center gap-2 text-sm active:scale-95 transition">
                  <MessageCircle className="w-4 h-4" /> Email the host
                </a>
              ) : (
                <span className="shrink-0 text-sm text-ink-faint">Contact shared after booking</span>
              )}
            </div>

            {/* Things to know */}
            <h3 className="mt-8 font-heading font-bold text-lg text-ink">Things to know</h3>
            <div className="mt-4 grid grid-cols-3 gap-6 pt-6 border-t border-black/10 text-xs text-ink-soft" data-testid="ddp-policies">
              <div>
                <p className="flex items-center gap-1.5 font-semibold text-ink text-sm mb-2"><ShieldQuestion className="w-4 h-4 text-brand" /> Cancellation</p>
                <p className="leading-relaxed">Free cancellation up to 48 hours before check-in. See full policy at checkout.</p>
              </div>
              <div>
                <p className="flex items-center gap-1.5 font-semibold text-ink text-sm mb-2"><DoorOpen className="w-4 h-4 text-brand" /> House rules</p>
                <p className="leading-relaxed">Check-in after 1:00 PM · Checkout before 11:00 AM · No smoking indoors.</p>
              </div>
              <div>
                <p className="flex items-center gap-1.5 font-semibold text-ink text-sm mb-2"><Lock className="w-4 h-4 text-brand" /> Safety & property</p>
                <p className="leading-relaxed">Exterior security cameras present · First-aid kit available on site.</p>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT sidebar */}
        <aside className="col-span-5">
          {rooms.length > 0 ? (
            <ReservationSidebar rooms={rooms} propertyId={propertyId} rev={rev} sp={sp} />
          ) : (
            <div className="rounded-3xl border border-black/10 bg-white shadow-xl p-6 sticky top-24 text-center" data-testid="ddp-no-rooms">
              <p className="text-ink font-medium">No rooms are listed yet for this stay.</p>
              {p.phone && <a href={`tel:${p.phone}`} className="mt-4 inline-block rounded-xl bg-ink text-white px-5 py-2.5 text-sm font-semibold">Call host</a>}
            </div>
          )}
        </aside>
      </div>

      <Footer />

      <Lightbox images={images} open={lightOpen} index={lightIdx} onClose={() => setLightOpen(false)} onIndex={setLightIdx} />
      <AmenitiesModal open={amenitiesOpen} onOpenChange={setAmenitiesOpen} amenities={allAmenities} />
    </div>
  );
};

const ReviewCard = ({ r }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (r.text || "").length > 180;
  return (
    <div className="bg-white border border-black/10 rounded-2xl p-4 shadow-sm space-y-2.5" data-testid="ddp-review-card">
      <div className="flex items-center gap-2.5">
        {r.profile_photo_url ? (
          <img src={r.profile_photo_url} alt={r.author_name} referrerPolicy="no-referrer" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center text-sm font-bold">{(r.author_name || "G").slice(0, 1).toUpperCase()}</div>
        )}
        <div className="min-w-0">
          <p className="font-heading font-semibold text-sm text-ink truncate">{r.author_name || "Google user"}</p>
          <p className="text-[11px] text-ink-faint">{r.relative_time_description}</p>
        </div>
        <span className="ml-auto"><Stars value={r.rating} /></span>
      </div>
      {r.text && (
        <p className={`text-[13px] text-ink-soft leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>{r.text}</p>
      )}
      {long && (
        <button onClick={() => setExpanded((v) => !v)} className="text-xs font-semibold text-ink underline underline-offset-2">
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
};

export default PropertyDetailDesktop;
